import { motion } from 'framer-motion'
import { Badge } from '@/components/ui/badge'

interface PersonalInfo {
  fullName?: string
  firstName?: string
  lastName?: string
  nickName?: string
  dob?: string
  gender?: string
  age?: string
  nationality?: string
}

interface ContactDetails {
  email?: string
  phone?: string
  alternativeEmails?: string
  phoneNumbers?: string
}

interface SocialMedia {
  linkedin?: string
  facebook?: string
  twitter?: string
  instagram?: string
  vk?: string
  socialProfiles?: string
}

interface Addresses {
  street?: string
  city?: string
  state?: string
  country?: string
  postCode?: string
  latitude?: string
  longitude?: string
  fullAddress?: string
}

interface GovernmentIDs {
  ssn?: string
  tin?: string
  passport?: string
  driverLicense?: string
  nationalID?: string
}

interface Photo {
  url: string
  source: string
  type: string
}

interface IdentityProfileCardProps {
  personalInfo: PersonalInfo
  contactDetails: ContactDetails
  socialMedia: SocialMedia
  addresses: Addresses
  governmentIDs: GovernmentIDs
  photos: Photo[]
  query: string
  showSensitive?: boolean
}

function maskSensitive(value: string, show: boolean): string {
  if (show || !value) return value
  if (value.includes('@')) {
    const [local, domain] = value.split('@')
    return `${local.slice(0, 2)}${'*'.repeat(Math.max(local.length - 2, 2))}@${domain}`
  }
  if (value.length > 6) {
    return value.slice(0, 3) + '*'.repeat(value.length - 5) + value.slice(-2)
  }
  return '*'.repeat(value.length)
}

function DataField({ label, value, sensitive, showSensitive, source }: {
  label: string
  value?: string
  sensitive?: boolean
  showSensitive?: boolean
  source?: string
}) {
  if (!value) return null
  const displayed = sensitive ? maskSensitive(value, showSensitive || false) : value
  return (
    <div className="bg-black/40 border border-gray-700/50 rounded-lg p-3 hover:border-gray-600/50 transition-colors">
      <div className="flex items-center justify-between mb-1">
        <span className="text-gray-400 text-xs font-mono uppercase tracking-wider">{label}</span>
        {source && (
          <span className="text-gray-600 text-[10px] font-mono">{source}</span>
        )}
      </div>
      <div className="text-white text-sm font-medium break-all">{displayed}</div>
    </div>
  )
}

const SOCIAL_ICONS: Record<string, { label: string; color: string; bgColor: string }> = {
  linkedin: { label: 'LinkedIn', color: 'text-blue-400', bgColor: 'bg-blue-900/30 border-blue-500/30' },
  facebook: { label: 'Facebook', color: 'text-blue-400', bgColor: 'bg-blue-800/30 border-blue-500/30' },
  twitter: { label: 'Twitter/X', color: 'text-sky-400', bgColor: 'bg-sky-900/30 border-sky-500/30' },
  instagram: { label: 'Instagram', color: 'text-pink-400', bgColor: 'bg-pink-900/30 border-pink-500/30' },
  vk: { label: 'VK', color: 'text-blue-300', bgColor: 'bg-blue-900/30 border-blue-500/30' },
}

export function IdentityProfileCard({
  personalInfo,
  contactDetails,
  socialMedia,
  addresses,
  governmentIDs,
  photos,
  query,
  showSensitive = false,
}: IdentityProfileCardProps) {
  const hasPersonal = Object.values(personalInfo).some(Boolean)
  const hasContact = Object.values(contactDetails).some(Boolean)
  const hasAddress = Object.values(addresses).some(Boolean)
  const hasGovtIDs = Object.values(governmentIDs).some(Boolean)
  const hasSocial = Object.values(socialMedia).some(Boolean)
  const hasPhotos = photos && photos.length > 0

  if (!hasPersonal && !hasContact && !hasAddress && !hasGovtIDs) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="border border-cyan-500/30 rounded-lg bg-gradient-to-br from-gray-900/80 via-black to-gray-900/80 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-800/50 bg-cyan-900/10">
        <div className="flex items-center gap-3">
          {/* Profile photo if available */}
          {hasPhotos ? (
            <div className="w-14 h-14 rounded-lg overflow-hidden border-2 border-cyan-500/40 flex-shrink-0">
              <img
                src={photos[0].url}
                alt="Profile"
                className="w-full h-full object-cover"
                crossOrigin="anonymous"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  const img = e.target as HTMLImageElement
                  if (photos[0].url.includes('googleusercontent.com') && !photos[0].url.includes('=s')) {
                    img.src = photos[0].url.split('?')[0] + '=s128-c'
                  } else {
                    img.style.display = 'none'
                  }
                }}
              />
            </div>
          ) : (
            <div className="w-14 h-14 rounded-lg bg-cyan-900/30 border-2 border-cyan-500/30 flex items-center justify-center flex-shrink-0">
              <span className="text-cyan-400 text-2xl font-bold">
                {(personalInfo.fullName || query || '?')[0].toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <h3 className="text-white font-semibold text-lg">
              {personalInfo.fullName || personalInfo.firstName && personalInfo.lastName
                ? `${personalInfo.firstName} ${personalInfo.lastName}`
                : query}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              {personalInfo.nationality && (
                <span className="text-gray-400 text-xs">{personalInfo.nationality}</span>
              )}
              {addresses.city && addresses.country && (
                <span className="text-gray-400 text-xs">
                  {addresses.city}, {addresses.country}
                </span>
              )}
            </div>
          </div>
        </div>
        <Badge className="bg-cyan-600/30 text-cyan-300 border border-cyan-500/30 font-mono text-xs">
          AGGREGATED PROFILE
        </Badge>
      </div>

      <div className="p-4 space-y-4">
        {/* Personal Info */}
        {hasPersonal && (
          <div>
            <div className="text-cyan-400 font-mono text-xs tracking-wider mb-2 font-semibold">PERSONAL DATA</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <DataField label="Full Name" value={personalInfo.fullName} />
              <DataField label="Nickname" value={personalInfo.nickName} />
              <DataField label="Date of Birth" value={personalInfo.dob} />
              <DataField label="Gender" value={personalInfo.gender} />
              <DataField label="Age" value={personalInfo.age} />
              <DataField label="Nationality" value={personalInfo.nationality} />
            </div>
          </div>
        )}

        {/* Contact Info */}
        {hasContact && (
          <div>
            <div className="text-blue-400 font-mono text-xs tracking-wider mb-2 font-semibold">CONTACT INTELLIGENCE</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <DataField label="Email" value={contactDetails.email} sensitive showSensitive={showSensitive} />
              <DataField label="Phone" value={contactDetails.phone} sensitive showSensitive={showSensitive} />
              {contactDetails.alternativeEmails && (
                <DataField label="Alt. Emails" value={contactDetails.alternativeEmails} sensitive showSensitive={showSensitive} />
              )}
              {contactDetails.phoneNumbers && (
                <DataField label="Other Phones" value={contactDetails.phoneNumbers} sensitive showSensitive={showSensitive} />
              )}
            </div>
          </div>
        )}

        {/* Address */}
        {hasAddress && (
          <div>
            <div className="text-green-400 font-mono text-xs tracking-wider mb-2 font-semibold">LOCATION INTELLIGENCE</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <DataField label="Street" value={addresses.street} />
              <DataField label="City" value={addresses.city} />
              <DataField label="State" value={addresses.state} />
              <DataField label="Country" value={addresses.country} />
              <DataField label="Postal Code" value={addresses.postCode} />
              {addresses.fullAddress && <DataField label="Full Address" value={addresses.fullAddress} />}
            </div>
          </div>
        )}

        {/* Government IDs */}
        {hasGovtIDs && (
          <div>
            <div className="text-amber-400 font-mono text-xs tracking-wider mb-2 font-semibold">DOCUMENT / ID DATA</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <DataField label="SSN" value={governmentIDs.ssn} sensitive showSensitive={showSensitive} />
              <DataField label="TIN" value={governmentIDs.tin} sensitive showSensitive={showSensitive} />
              <DataField label="Passport" value={governmentIDs.passport} sensitive showSensitive={showSensitive} />
              <DataField label="Driver License" value={governmentIDs.driverLicense} sensitive showSensitive={showSensitive} />
              <DataField label="National ID" value={governmentIDs.nationalID} sensitive showSensitive={showSensitive} />
            </div>
          </div>
        )}

        {/* Social Media */}
        {hasSocial && (
          <div>
            <div className="text-indigo-400 font-mono text-xs tracking-wider mb-2 font-semibold">SOCIAL MEDIA</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(socialMedia).map(([key, value]) => {
                if (!value || key === 'socialProfiles') return null
                const config = SOCIAL_ICONS[key]
                if (!config) return null
                return (
                  <a
                    key={key}
                    href={value.startsWith('http') ? value : `https://${value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${config.bgColor} hover:brightness-125 transition-all text-sm`}
                  >
                    <span className={config.color}>{config.label}</span>
                  </a>
                )
              })}
            </div>
          </div>
        )}

        {/* Photos gallery */}
        {hasPhotos && photos.length > 1 && (
          <div>
            <div className="text-pink-400 font-mono text-xs tracking-wider mb-2 font-semibold">PHOTOS & AVATARS</div>
            <div className="flex flex-wrap gap-2">
              {photos.map((photo, i) => (
                <div key={i} className="w-16 h-16 rounded-lg overflow-hidden border border-gray-700/50">
                  <img
                    src={photo.url}
                    alt={`Photo ${i + 1}`}
                    className="w-full h-full object-cover"
                    crossOrigin="anonymous"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  )
}
